/**
 * Enhanced Trystero room creation for mrmr.
 *
 * Derives the room ID cryptographically from the meeting code using
 * HMAC-SHA256, then joins via Nostr relays with configured ICE servers.
 */

import { joinRoom as trysteroJoin } from 'trystero/nostr'
import type { Room } from 'trystero'
import { deriveRoomId } from './invite'
import {
  APP_ID,
  NOSTR_RELAYS,
  NOSTR_RELAY_REDUNDANCY,
  ICE_SERVERS,
} from '../constants'

export interface MeetingRoom {
  room: Room
  roomId: string
  meetingCode: string
}

/**
 * Create a meeting room from a meeting code.
 *
 * 1. Derives a room ID via HMAC-SHA256 of the meeting code
 * 2. Selects Nostr relays based on NOSTR_RELAY_REDUNDANCY
 * 3. Joins the Trystero/Nostr room with configured ICE servers
 */
export async function createMeetingRoom(
  meetingCode: string,
  onError?: (details: { error: string }) => void,
): Promise<MeetingRoom> {
  const roomId = await deriveRoomId(meetingCode)

  const relays = NOSTR_RELAYS.slice(0, NOSTR_RELAY_REDUNDANCY)

  const room = trysteroJoin(
    {
      appId: APP_ID,
      relayUrls: relays as string[],
      rtcConfig: {
        iceServers: ICE_SERVERS as RTCIceServer[],
      },
    },
    roomId,
    onError as never,
  )

  return { room, roomId, meetingCode }
}

/**
 * Legacy-compatible room creation using a raw room ID.
 * Prefer createMeetingRoom() for new code.
 */
export function createRoom(
  roomId: string,
  onError?: (details: { error: string }) => void,
): Room {
  return trysteroJoin(
    {
      appId: APP_ID,
      rtcConfig: {
        iceServers: ICE_SERVERS as RTCIceServer[],
      },
    },
    roomId,
    onError as never,
  )
}
